import { Link, useLocation } from "react-router-dom";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Home, Tv, Layers, Grid3X3, Globe, Heart, Clock, Crown, Settings, HelpCircle, ChevronLeft, ChevronRight, FileText, Languages } from "lucide-react";
import { SettingsContext } from "@/App";
import { cn } from "@/lib/utils";
import { useSiteNavigation, pageLabel } from "@/hooks/useSiteNavigation";

export default function Sidebar() {
  const { settings } = useContext(SettingsContext);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const leftPages = useSiteNavigation("left_menu");

  const navItems = [
    { icon: Home, label: t("nav.home"), path: "/" },
    { icon: Tv, label: t("nav.liveTv"), path: "/live" },
    { icon: Layers, label: t("nav.categories"), path: "/categories" },
    { icon: Globe, label: t("nav.countries"), path: "/countries" },
    { icon: Languages, label: t("nav.languages", "Languages"), path: "/languages" },
    { icon: Heart, label: t("nav.favorites"), path: "/account?tab=favorites" },
    { icon: Clock, label: t("nav.history"), path: "/account?tab=overview" },
  ];

  const bottomItems = [
    { icon: Crown, label: t("nav.premium"), path: "/membership", gold: true },
    { icon: Settings, label: t("nav.settings"), path: "/account?tab=settings" },
    { icon: HelpCircle, label: t("nav.support"), path: "/account?tab=suggest" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/" && !location.search;
    return location.pathname + location.search === path || location.pathname === path;
  };

  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/moderator")) return null;

  return (
    <aside
      className={cn(
        "hidden lg:flex flex-col sticky left-0 top-0 h-screen z-40 border-r transition-all duration-300",
        "bg-[#0A0F1E]/95 backdrop-blur-xl border-white/[0.06]",
        collapsed ? "w-[72px]" : "w-[220px]"
      )}
      style={{ alignSelf: 'start' }}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-24 flex items-center px-3 border-b border-white/[0.06]">
        <Link to="/" className="flex items-center group" data-testid="sidebar-logo">
          <img
            src="/djilu-logo.png"
            alt="DJILU.COM"
            className={cn(
              "object-contain transition-all duration-300",
              collapsed ? "h-10 w-10" : "h-[4.5rem] w-auto max-w-[200px]"
            )}
          />
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {!collapsed && <div className="text-[10px] font-bold text-[#94A3B8]/40 uppercase tracking-[0.15em] px-3 mb-2">Menu</div>}
        <div className="space-y-1">
          {navItems.map(item => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                  active ? "bg-white/[0.06] text-white" : "text-[#94A3B8] hover:text-white hover:bg-white/[0.04]",
                  collapsed && "justify-center px-0"
                )}
              >
                {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#FFC928] rounded-r-full" />}
                <item.icon size={18} className={cn(active && "text-[#FFC928]")} />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </div>

        {leftPages.length > 0 && (
          <div className="mt-5 space-y-1" data-testid="sidebar-pages">
            {!collapsed && <div className="text-[10px] font-bold text-[#94A3B8]/40 uppercase tracking-[0.15em] px-3 mb-2">Pages</div>}
            {leftPages.map(p => {
              const label = pageLabel(p, i18n);
              const active = location.pathname === p.url;
              const cls = cn(
                "relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                active ? "bg-white/[0.06] text-white" : "text-[#94A3B8] hover:text-white hover:bg-white/[0.04]",
                collapsed && "justify-center px-0"
              );
              return p.new_tab ? (
                <a key={p.slug} href={p.url} target="_blank" rel="noopener noreferrer" className={cls} title={label} data-testid={`sidebar-page-${p.slug}`}>
                  <FileText size={18} className={cn(active && "text-[#FFC928]")} />
                  {!collapsed && <span>{label}</span>}
                </a>
              ) : (
                <Link key={p.slug} to={p.url} className={cls} title={label} data-testid={`sidebar-page-${p.slug}`}>
                  {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-[#FFC928] rounded-r-full" />}
                  <FileText size={18} className={cn(active && "text-[#FFC928]")} />
                  {!collapsed && <span>{label}</span>}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {/* Bottom nav */}
      <div className="px-3 py-4 border-t border-white/[0.06] space-y-1">
        {bottomItems.map(item => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
                item.gold ? "text-[#FFC928] hover:bg-[#FFC928]/10" :
                active ? "bg-white/[0.06] text-white" : "text-[#94A3B8] hover:text-white hover:bg-white/[0.04]",
                collapsed && "justify-center px-0"
              )}
            >
              <item.icon size={18} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-[#0F172A] border border-white/[0.1] rounded-full flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-[#1e293b] transition-colors z-50"
        data-testid="sidebar-collapse-btn"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}
